// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/jsx-sort-props */
import "./index.less";

import React, { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import { CustomModal } from "../CustomModal";
import { useTranslate } from "@obsbot/remo-i18n";
import { InputWithError } from "../InputWithErrorMsg";
import { eventBus, filterInputValue, limitInputLength } from "@obsbot/remo-utils";
import { withMessage } from "../../HOC/withMessage";
import { SettingSlider } from "../SettingPage/SettingSlider";
import { SettingSwitch } from "../SettingPage/SettingSwitch";
import { ChannelItem, useChannelRoomStore, useChannelStore } from "@obsbot/remo-stores";
import classNames from "classnames";

type ChannelEditType = Pick<
    ChannelItem,
    "channelName" | "channelUuid" | "timeInterval" | "maxPlayer"
>;

export interface ChannelSettingModalProps {
    visible: boolean;
    channelInfo: ChannelEditType;
    onDone: (payload: ChannelEditType) => void;
    setVisible: (flag: boolean) => void;
}

const matchRange: Record<number, number> = {
    0: 0,
    1: 5,
    2: 10,
    3: 15,
    4: 30,
    5: 60,
};
const valueToRange: Record<number, number> = {
    0: 0,
    5: 1,
    10: 2,
    15: 3,
    30: 4,
    60: 5,
};

export const ChannelSettingModal: React.FC<ChannelSettingModalProps> = withMessage(
    ({ visible, channelInfo, message, onDone, setVisible }): ReactElement => {
        const channelroomStore = useChannelRoomStore();
        const channelStore = useChannelStore();
        const t = useTranslate();
        const modalRef = useRef<HTMLDivElement | null>(null);
        // 是否正在输入拼音
        const isComposing = useRef(false);
        // 记录当前操作的频道名称
        const [currentChannelName, setCurrentChannelName] = useState<string>(
            channelInfo.channelName,
        );
        const [isEditingChannelName, setIsEditingChannelName] = useState<boolean>(false);
        // 消息间隔时间
        const [messageInterval, setMessageInterval] = useState<number>(channelInfo.timeInterval);
        const inputIntervalTime = useMemo(() => {
            return valueToRange[messageInterval];
        }, [messageInterval]);
        // 最大频道人数
        const [maxChannelMember, setMaxChannelMember] = useState<number>(channelInfo.maxPlayer);
        const [isLimitChannelMember, setIsLimitChannelMember] = useState<boolean>(
            channelInfo.maxPlayer && channelInfo.maxPlayer > 0 ? true : false,
        );
        const minChannelMember = useMemo(() => {
            return (
                (channelroomStore.isJoinedRTC
                    ? channelroomStore.users?.cachedUsers.size
                    : channelStore.channelMembers.get(channelInfo.channelUuid)?.length) ?? 1
            );
        }, [channelroomStore, channelStore, channelInfo.channelUuid]);
        const lastWarnTimeRef = useRef(0);
        // 是否提示未保存
        const [isPrompt, setIsPrompt] = useState<boolean>(false);
        const [isPromptDialog, setIsPromptDialog] = useState<boolean>(false);
        const isFirstIn = useRef(true);

        useEffect(() => {
            setCurrentChannelName(channelInfo.channelName);
            setMaxChannelMember(channelInfo.maxPlayer);
            setMessageInterval(channelInfo.timeInterval);
            setIsLimitChannelMember(
                channelInfo.maxPlayer && channelInfo.maxPlayer > 0 ? true : false,
            );
            setIsEditingChannelName(false);
        }, [visible, channelInfo]);

        useEffect(() => {
            setMessageInterval(channelInfo.timeInterval);
        }, [channelInfo.timeInterval]);

        useEffect(() => {
            setMaxChannelMember(isLimitChannelMember ? 6 : 0);
        }, [isLimitChannelMember]);
        // 处理onDone事件
        const handleOnDone = (): void => {
            if (!currentChannelName) {
                message.warning(t("noInputSingle", { text1: t("channelName") }));
                return;
            }
            const editInfo: ChannelEditType = {
                channelName: currentChannelName,
                channelUuid: channelInfo.channelUuid,
                maxPlayer: isLimitChannelMember ? maxChannelMember : 0,
                timeInterval: messageInterval,
            };
            onDone?.(editInfo);
        };
        // 处理修改channelName
        const handleEditChannelName = (): void => {
            if (currentChannelName === "") {
                message.warning(t("noInputSingle", { text1: t("username") }));
                return;
            }
            setIsEditingChannelName(!isEditingChannelName);
        };
        // 处理点击关闭
        const handleClickClose = (): void => {
            if (
                currentChannelName !== channelInfo.channelName ||
                messageInterval !== channelInfo.timeInterval ||
                maxChannelMember !== channelInfo.maxPlayer
            ) {
                const modalContainer =
                    modalRef.current?.parentNode?.parentNode?.parentNode?.parentNode;
                if (modalContainer) {
                    const handleAnimationend = (): void => {
                        modalContainer.removeEventListener("animationend", handleAnimationend);
                        setIsPrompt(false);
                    };
                    modalContainer.addEventListener("animationend", handleAnimationend);
                    setIsPrompt(true);
                    setIsPromptDialog(true);
                    return;
                }
            }
            setVisible?.(false);
        };
        useEffect(() => {
            if (!isFirstIn.current && !isPromptDialog) {
                setVisible?.(false);
            }
            isFirstIn.current = false;
        }, [isPromptDialog, setVisible]);
        return (
            <CustomModal
                closeWhenClickCloseBtn={false}
                open={visible}
                renderHeader={
                    <div className="modal-header action-modal-header text-shadow">
                        <span>{t("channelSetting")}</span>
                    </div>
                }
                setVisible={setVisible}
                onClickClose={handleClickClose}
                className={classNames("ChannelSettingModal", {
                    "ChannelSettingModal-prompt": isPrompt,
                })}
                // closable={isEditOrCreate}
                footer={
                    <div className="modal-button-group">
                        <div
                            className="modal-button-item button-normal"
                            onClick={() => {
                                setVisible?.(false);
                            }}
                        >
                            {t("cancel")}
                        </div>
                        <div className="modal-button-item" onClick={() => handleOnDone?.()}>
                            {t("settings.hotkey-setting.save")}
                        </div>
                    </div>
                }
            >
                <div className="action-modal-content" ref={modalRef}>
                    <div className="channel-setting-name">
                        <div className="action-modal-form-title">{t("channelName")}</div>
                        <InputWithError
                            suffix={
                                <div
                                    className={
                                        isEditingChannelName
                                            ? "setting-edit setting-edit-confirm"
                                            : "setting-edit"
                                    }
                                    onClick={handleEditChannelName}
                                >
                                    {isEditingChannelName
                                        ? t("settings.account-setting.confirm")
                                        : t("settings.account-setting.edit")}
                                </div>
                            }
                            readOnly={!isEditingChannelName}
                            className={classNames("action-modal-form-input", {
                                "action-modal-form-input-active": isEditingChannelName,
                            })}
                            errorMsg={t("createChannelFail")}
                            isError={false}
                            maxLength={16}
                            placeholder={t("enterChannelName")}
                            status={""}
                            value={currentChannelName}
                            onChange={e => {
                                let filterVal = filterInputValue(e.target.value);
                                if (!isComposing.current) {
                                    const { val, overLimit } = limitInputLength(filterVal);
                                    if (overLimit) {
                                        eventBus.emit("show-message", {
                                            message: t("nameLengthOverLimit"),
                                            type: "warning",
                                        });
                                    }
                                    filterVal = val;
                                }
                                setCurrentChannelName(filterVal);
                            }}
                            onCompositionEnd={e => {
                                isComposing.current = false;
                                const finalValue = (e.target as any).value;
                                const { val, overLimit } = limitInputLength(finalValue);
                                if (overLimit) {
                                    eventBus.emit("show-message", {
                                        message: t("nameLengthOverLimit"),
                                        type: "warning",
                                    });
                                }
                                setCurrentChannelName(val);
                            }}
                            onCompositionStart={() => (isComposing.current = true)}
                            onKeyDown={e => {
                                if (e.key === "Enter") {
                                    handleOnDone?.();
                                }
                            }}
                        />
                    </div>
                    <div className="channel-setting-interval-time">
                        <div className="action-modal-form-title">
                            {t("channel.message-interval-time")}
                        </div>
                        <div className="action-modal-form-tip">
                            {t("channel.message-interval-time-tip")}
                        </div>
                        <SettingSlider
                            rootClassName="switch-line"
                            step={null}
                            marks={{
                                0: "0s",
                                1: "5s",
                                2: "10s",
                                3: "15s",
                                4: "30s",
                                5: "1min",
                            }}
                            bg_track="var(--main-violet-1)"
                            bg_handle="transparent"
                            tooltip={{ formatter: null }}
                            max={5}
                            value={inputIntervalTime}
                            onChange={value => setMessageInterval(matchRange[value])}
                        />
                    </div>
                    <div className="channel-setting-max-count">
                        <div className="channel-setting-limit-max">
                            <div className="description">
                                <div className="action-modal-form-title">
                                    {t("channel.limit-channel-count")}
                                </div>
                                <div className="action-modal-form-tip">
                                    {t("channel.limit-channel-count-tip")}
                                </div>
                            </div>
                            <div className="switch">
                                <SettingSwitch
                                    checked={isLimitChannelMember}
                                    onChange={() => setIsLimitChannelMember(!isLimitChannelMember)}
                                />
                            </div>
                        </div>
                        <SettingSlider
                            rootClassName="switch-line"
                            disabled={!isLimitChannelMember}
                            marks={{ 1: 1, 6: 6, 12: 12 }}
                            step={1}
                            tooltip={{ rootClassName: "switch-line-tooltip", arrow: false }}
                            bg_track="var(--main-violet-1)"
                            bg_handle="transparent"
                            max={12}
                            min={1}
                            value={maxChannelMember}
                            onChange={value => {
                                const now = Date.now();
                                if (now - lastWarnTimeRef.current < 500) {
                                    return;
                                }
                                if (value < minChannelMember) {
                                    eventBus.emit("show-message", {
                                        message: t("channel.limit-channel-min-count"),
                                        type: "warning",
                                    });
                                    lastWarnTimeRef.current = now;
                                    return;
                                }
                                setMaxChannelMember(value);
                            }}
                        />
                    </div>
                </div>
                <CustomModal
                    open={isPromptDialog}
                    renderHeader={
                        <div className="modal-header action-modal-header text-shadow">
                            <span>{t("channel.unsave-editing")}</span>
                        </div>
                    }
                    width={392}
                    onClickClose={() => {
                        setIsPromptDialog(false);
                    }}
                    setVisible={setIsPromptDialog}
                    className="ChannelSettingPromptSaveDialog"
                    footer={
                        <div className="modal-button-group">
                            <div
                                className="modal-button-item button-normal"
                                onClick={() => {
                                    setIsPromptDialog(false);
                                }}
                            >
                                {t("channel.drop-edit")}
                            </div>
                            <div
                                className="modal-button-item"
                                onClick={() => {
                                    handleOnDone?.();
                                    setIsPromptDialog(false);
                                }}
                            >
                                {t("channel.save")}
                            </div>
                        </div>
                    }
                >
                    <div className="action-modal-content action-modal-warn-text">
                        <div className="action-modal-form-title">
                            {t("channel.unsave-editing-text")}
                        </div>
                    </div>
                </CustomModal>
            </CustomModal>
        );
    },
);
